// @ts-nocheck
/**
 * App-shell account layer. Everything works logged out (localStorage); a customer account only adds
 * a copy of likes / skips in customer metafields via POST /api/account (repo vhp-planner).
 *
 *  - local likes live in assets/vhp-planner-likes.js (vhp_planner_anon); goals are the meal planner's own
 *  - logged in: merge both ways on load (and ~8s after a change), newest timestamp per recipe wins
 *  - logged out: after 10 likes a soft "Speicher deine Likes" prompt (login/register = Shopify customer accounts)
 *  - saver (docs/email/SIGNUPS.md, 2026-11-03): saving a recipe with her e-mail creates a customer in the background and
 *    hands this browser its id + token (localStorage vhp_guest_account, assets/vhp-mail-signup.js). The same sync then
 *    runs without a login; on another device she logs in with the e-mail code and finds them.
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpAccount) return;

  const configEl = document.getElementById('vhp-account-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const PROMPT_KEY = 'vhp_save_prompt_dismissed_at';
  const SESSION_SYNC_KEY = 'vhp_acct_session_sync';
  const TOKEN_RELOAD_KEY = 'vhp_token_reload';
  const CTA_KEY = 'vhp_save_prompt_cta';
  const PROMPT_AT = 10;
  const PROMPT_SNOOZE_MS = 7 * 24 * 3600 * 1000;
  const PUSH_DELAY_MS = 8000; // longer than the "Rückgängig" toast on the Likes page

  const likes = () => window.vhpPlannerLikes || null;
  const loggedIn = Boolean(config.customerId);

  // The background account from a signup on this browser (null when logged in: the login's own token wins).
  const GUEST_KEY = 'vhp_guest_account';
  const guest = () => {
    if (loggedIn) return null;
    try {
      const raw = JSON.parse(localStorage.getItem(GUEST_KEY) || 'null');
      return raw && /^\d+$/.test(String(raw.customerId)) && typeof raw.token === 'string' && raw.token ? raw : null;
    } catch (e) {
      return null;
    }
  };
  const dropGuest = () => {
    try {
      localStorage.removeItem(GUEST_KEY);
    } catch (e) {
      /* ignore */
    }
  };
  // Whose likes this browser syncs: the login, else the background account, else nobody (local only).
  const creds = () => {
    if (loggedIn) return config.token ? { customerId: config.customerId, token: config.token } : null;
    return guest();
  };
  const linked = () => loggedIn || Boolean(guest());

  const emit = (name, detail) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (e) {
      /* ignore */
    }
  };

  /* ------------------------------------------------------------ sync */

  const api = (path, body) =>
    fetch(config.apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  // The customer token is created server side and printed by Liquid on the next load.
  const ensureToken = async () => {
    if (!loggedIn) return false;
    if (config.token) {
      sessionStorage.removeItem(TOKEN_RELOAD_KEY);
      return true;
    }
    if (sessionStorage.getItem(TOKEN_RELOAD_KEY)) return false;
    try {
      await api('/api/token', { customerId: config.customerId });
      sessionStorage.setItem(TOKEN_RELOAD_KEY, '1');
      location.reload();
    } catch (e) {
      /* try again next page */
    }
    return false;
  };

  let status = linked() ? 'idle' : 'local'; // local | idle | syncing | synced | error
  let syncing = null;
  let pushTimer = 0;

  const setStatus = (next) => {
    status = next;
    emit('vhp:account-status', { status });
  };

  const sync = async () => {
    const who = creds();
    if (!who || !likes()) return null;
    if (syncing) return syncing;
    setStatus('syncing');
    syncing = (async () => {
      try {
        const response = await api('/api/account', {
          customerId: who.customerId,
          token: who.token,
          likes: likes().exportDoc(),
        });
        // The background account is gone or its key no longer fits: back to local, the "!" returns.
        if (!loggedIn && [400, 401, 404].includes(response.status)) {
          dropGuest();
          setStatus('local');
          paintBadge();
          return null;
        }
        if (!response.ok) throw new Error(`account ${response.status}`);
        const data = await response.json();
        if (data.likes) {
          // Merge again with what changed locally while the request was in flight.
          likes().applyDoc(likes().mergeDocs(likes().exportDoc(), data.likes));
        }
        try {
          sessionStorage.setItem(SESSION_SYNC_KEY, '1');
        } catch (e) {
          /* ignore */
        }
        setStatus('synced');
        emit('vhp:account-synced', {});
        return data;
      } catch (e) {
        setStatus('error');
        return null;
      } finally {
        syncing = null;
      }
    })();
    return syncing;
  };

  const schedulePush = () => {
    if (!creds()) return;
    window.clearTimeout(pushTimer);
    pushTimer = window.setTimeout(sync, PUSH_DELAY_MS);
  };

  // Flush pending changes when the page goes away (best effort).
  const flush = () => {
    const who = creds();
    if (!pushTimer || !who || !likes() || !navigator.sendBeacon) return;
    window.clearTimeout(pushTimer);
    pushTimer = 0;
    try {
      const blob = new Blob(
        [JSON.stringify({ customerId: who.customerId, token: who.token, likes: likes().exportDoc() })],
        { type: 'text/plain' }
      );
      // text/plain keeps this a "simple" request (no preflight); the API parses the body as JSON either way.
      navigator.sendBeacon(config.apiBase + '/api/account', blob);
    } catch (e) {
      /* ignore */
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  /* ------------------------------------------------------------ login + prompt */

  const loginUrl = (returnTo) => {
    const base = config.loginUrl || '/account/login';
    const target = returnTo || location.pathname + location.search;
    return base + (base.includes('?') ? '&' : '?') + 'return_to=' + encodeURIComponent(target);
  };

  const snoozed = () => {
    try {
      const at = Number(localStorage.getItem(PROMPT_KEY));
      return Number.isFinite(at) && at > 0 && Date.now() - at < PROMPT_SNOOZE_MS;
    } catch (e) {
      return false;
    }
  };

  const snooze = () => {
    try {
      localStorage.setItem(PROMPT_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  };

  let promptEl = null;

  const hidePrompt = () => {
    if (!promptEl) return;
    const node = promptEl;
    promptEl = null;
    node.classList.remove('is-in');
    window.setTimeout(() => node.remove(), 300);
  };

  const showPrompt = () => {
    if (promptEl || linked() || snoozed()) return;
    // The Likes page shows its own inline card; the planner keeps its own flow free of it.
    if (document.querySelector('[data-likes], .vhp-planner-page')) return;
    promptEl = document.createElement('aside');
    promptEl.className = 'acct-prompt';
    promptEl.setAttribute('role', 'region');
    promptEl.setAttribute('aria-label', tr('account.save_likes'));
    promptEl.innerHTML =
      '<div class="acct-prompt__text"><strong>' + tr('account.prompt_title') + '</strong><span>' + tr('account.prompt_text') + '</span></div>' +
      '<a class="acct-prompt__cta" href="' + loginUrl() + '">' + tr('account.prompt_cta') + '</a>' +
      '<button type="button" class="acct-prompt__later" aria-label="' + tr('common.later') + '">&times;</button>';
    promptEl.querySelector('.acct-prompt__later').addEventListener('click', () => {
      snooze();
      hidePrompt();
    });
    promptEl.querySelector('.acct-prompt__cta').addEventListener('click', () => {
      markPromptClick('banner');
    });
    document.body.appendChild(promptEl);
    window.requestAnimationFrame(() => promptEl && promptEl.classList.add('is-in'));
    if (typeof window.vhpTrack === 'function') window.vhpTrack('save_likes_prompt_shown', { source: 'banner' });
  };

  const likeCount = () => (likes() ? likes().likeCount() : 0);

  // Tapping a "save your likes" button: count the click and remember it, so the next logged-in page load
  // (after the Shopify login round trip) can report the conversion once.
  const markPromptClick = (source) => {
    window.vhpTrack?.('save_likes_prompt_click', { source });
    try {
      localStorage.setItem(CTA_KEY, JSON.stringify({ source, at: Date.now() }));
    } catch (e) {
      /* ignore */
    }
  };

  const reportConversion = () => {
    if (!loggedIn) return;
    try {
      const raw = JSON.parse(localStorage.getItem(CTA_KEY) || 'null');
      if (!raw) return;
      localStorage.removeItem(CTA_KEY);
      if (Date.now() - Number(raw.at) < 2 * 3600 * 1000) {
        window.vhpTrack?.('save_likes_prompt_converted', { source: String(raw.source || 'unknown'), likes: likeCount() });
      }
    } catch (e) {
      /* ignore */
    }
  };

  /* ------------------------------------------------------------ tab badge */

  // Not saved anywhere but here (logged out, no background account) with likes: small amber "!" on the Likes tab.
  // Pulses once when a like is added, so it gets noticed without nagging.
  let badgeCount = -1;
  const paintBadge = () => {
    const badge = document.querySelector('[data-likes-warn]');
    if (!badge) return;
    if (linked()) {
      badge.hidden = true;
      return;
    }
    const n = likeCount();
    badge.hidden = n === 0;
    if (badgeCount >= 0 && n > badgeCount) {
      badge.classList.remove('is-pulse');
      void badge.offsetWidth; // restart the animation
      badge.classList.add('is-pulse');
    }
    badgeCount = n;
  };

  /* ------------------------------------------------------------ wiring */

  window.addEventListener('storage', (event) => {
    if (event.key === (likes()?.STORAGE_KEY || 'vhp_planner_anon')) paintBadge();
  });

  window.addEventListener('vhp:likes-changed', (event) => {
    paintBadge();
    if (event.detail?.silent) return;
    if (linked()) schedulePush();
    else if (likeCount() >= PROMPT_AT) window.setTimeout(showPrompt, 900);
  });

  // A signup just made the background account (assets/vhp-mail-signup.js): push this browser's likes at once.
  window.addEventListener('vhp:guest-account', () => {
    hidePrompt();
    paintBadge();
    setStatus('idle');
    sync();
  });

  // Switched in-page to Likes (assets/app-router.js): that page has its own card, the banner goes.
  window.addEventListener('vhp:view-show', () => {
    if (promptEl && document.querySelector('[data-likes]')) hidePrompt();
  });

  window.vhpAccount = {
    loggedIn,
    subscriber: Boolean(config.subscriber),
    plannerUrl: config.plannerUrl || '/pages/meal-planner',
    markPromptClick,
    email: config.email,
    accountUrl: config.accountUrl,
    logoutUrl: config.logoutUrl,
    loginUrl,
    PROMPT_AT,
    status: () => status,
    syncNow: sync,
    // Saved somewhere other than this browser: logged in, or the background account.
    saved: linked,
    guest: () => Boolean(guest()),
  };

  const init = async () => {
    reportConversion();
    paintBadge();
    if (loggedIn) {
      // Logged in on this browser: the login's token replaces the background account's.
      dropGuest();
      // One sync per browser session on load; later changes push themselves (schedulePush).
      if ((await ensureToken()) && !sessionStorage.getItem(SESSION_SYNC_KEY)) sync();
    } else if (guest() && !sessionStorage.getItem(SESSION_SYNC_KEY)) sync();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
